import { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  getMyRallyState,
  updateMyRallyPosition,
  claimCheckpoint,
  claimFinish,
  getRallyLeaderboard,
  getRallyOtherDriverPositions,
  applyPendingAttack,
  getRallyBonusLog,
} from '../../../services/ghostRallyService';
import RallyBonusLog from './RallyBonusLog';
import {
  playCheckpointChime,
  playBonusFanfare,
  playNoEffectBlip,
  playSadAlert,
  playSlotTick,
} from '../../../utils/rallySounds';

const ALL_BONUS_TYPES = ['time_cut', 'big_time_cut', 'shield', 'add_1_min', 'add_2_min', 'no_effect'];
const SPIN_TOTAL_MS = 4200;

const MAP_STYLES = [
  { id: 'satellite', url: 'mapbox://styles/mapbox/satellite-streets-v12', label: 'Satellite' },
  { id: 'dark', url: 'mapbox://styles/mapbox/dark-v11', label: 'Dark' },
  { id: 'light', url: 'mapbox://styles/mapbox/light-v11', label: 'Light' },
];

/**
 * Driver-side active rally screen. Fullscreen overlay opened from
 * GhostRallyEventBanner once the rally is 'active' and the user is
 * an active driver. Reuses navigator.geolocation directly (matches
 * the existing routes/useLiveLocation pattern); pings update
 * ghost_rally_drivers.last_location_* and trigger auto-claims when
 * the driver enters an unclaimed checkpoint's radius.
 */
export default function GhostRallyActive({ eventId, onClose, onToast }) {
  const [state, setState] = useState(null); // { rally, checkpoints, driver, claims }
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState(null); // { lat, lng, accuracy }
  const [elapsedSec, setElapsedSec] = useState(0);
  const [bonusModal, setBonusModal] = useState(null); // shown after a claim
  const [toastMsg, setToastMsg] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [lbTab, setLbTab] = useState('standings'); // 'standings' | 'log'
  const [mapStyleIdx, setMapStyleIdx] = useState(0); // index into MAP_STYLES, default satellite
  const [otherDrivers, setOtherDrivers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [spinDisplay, setSpinDisplay] = useState('time_cut'); // emoji shown during spin
  const [attackTarget, setAttackTarget] = useState(null); // { driver, bonus } pending confirm
  const [deployingAttack, setDeployingAttack] = useState(false);
  const [following, setFollowing] = useState(true); // map auto-follows user until they pan
  const [attacksDealt, setAttacksDealt] = useState({ count: 0, totalSec: 0 });
  const prevPenaltySecRef = useRef(0);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const meMarkerRef = useRef(null);
  const otherMarkersRef = useRef({}); // user_id -> marker
  const claimingRef = useRef(new Set()); // de-dupe in-flight claims
  const watchIdRef = useRef(null);
  const lastUploadAtRef = useRef(0);

  /* ─── Load state ─── */
  async function refresh() {
    const { data, error } = await getMyRallyState(eventId);
    if (error) { onToast?.('Error: ' + error); return; }
    setState(data);
    setLoading(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [eventId]);

  /* ─── Init map once state is loaded — defaults to satellite ─── */
  useEffect(() => {
    if (loading || !state || !mapContainerRef.current || mapRef.current) return;
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLES[0].url,
      center: [state.rally.start_location_lng, state.rally.start_location_lat],
      zoom: 13,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.on('load', () => {
      mapRef.current = map;
      drawCheckpointsAndRadii(map, state);
    });
    // Style swaps recreate sources/layers — re-apply checkpoint markers each time.
    map.on('style.load', () => {
      if (state) drawCheckpointsAndRadii(map, state);
    });
    // User-initiated pan / zoom turns off auto-follow until they tap Re-center.
    map.on('dragstart', (e) => { if (e?.originalEvent) setFollowing(false); });
    map.on('zoomstart', (e) => { if (e?.originalEvent) setFollowing(false); });
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  function handleCycleStyle() {
    if (!mapRef.current) return;
    const next = (mapStyleIdx + 1) % MAP_STYLES.length;
    setMapStyleIdx(next);
    mapRef.current.setStyle(MAP_STYLES[next].url);
  }

  function handleCenterOnMe() {
    if (!mapRef.current || !position) return;
    setFollowing(true);
    mapRef.current.flyTo({ center: [position.lng, position.lat], zoom: 15, duration: 700 });
  }

  /* ─── Defensive periodic state refresh — covers cases where the UI is stale
        (e.g. multiple drivers in the same car claiming simultaneously). ─── */
  useEffect(() => {
    if (!state?.rally?.id) return;
    if (state.driver?.status === 'finished') return; // no need to poll when done
    const id = setInterval(refresh, 7000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.rally?.id, state?.driver?.status]);

  /* ─── Poll other drivers' positions for the live map ─── */
  useEffect(() => {
    if (!state?.rally?.id) return;
    let alive = true;
    async function load() {
      const { data } = await getRallyOtherDriverPositions(state.rally.id);
      if (alive) setOtherDrivers(data || []);
    }
    load();
    const id = setInterval(load, 10000);
    return () => { alive = false; clearInterval(id); };
  }, [state?.rally?.id]);

  /* ─── Render OTHER drivers as markers; tappable while attack is pending ─── */
  useEffect(() => {
    if (!mapRef.current) return;
    const pendingAttack = state?.pending_attack || null;
    const tappable = !!pendingAttack;
    // Tear down all markers and rebuild — simplest way to refresh click handlers
    Object.values(otherMarkersRef.current).forEach(m => m.remove());
    otherMarkersRef.current = {};

    otherDrivers.forEach(d => {
      if (d.last_location_lat == null) return;
      const isFinished = d.status === 'finished';
      const canTarget = tappable && d.status === 'active';
      const el = document.createElement('div');
      el.style.cssText = `
        display: flex; flex-direction: column; align-items: center;
        cursor: ${canTarget ? 'crosshair' : 'default'};
        pointer-events: ${canTarget ? 'auto' : 'none'};
      `;
      el.innerHTML = `
        <div style="
          background: ${canTarget ? '#fbbf24' : (isFinished ? '#4A90D9' : '#f59e0b')};
          color: #0a0a0a;
          font-size: 10px; font-weight: 700;
          padding: 2px 6px; border-radius: 4px;
          white-space: nowrap;
          border: 1px solid rgba(0,0,0,0.3);
          margin-bottom: 2px;
          ${canTarget ? 'box-shadow: 0 0 0 3px rgba(251,191,36,0.4);' : ''}
        ">${escapeHTML(d.display_name || 'Driver')}${canTarget ? ' 🎯' : ''}</div>
        <div style="
          width: ${canTarget ? '18px' : '14px'}; height: ${canTarget ? '18px' : '14px'}; border-radius: 50%;
          background: ${canTarget ? '#fbbf24' : (isFinished ? '#4A90D9' : '#f59e0b')};
          border: ${canTarget ? '3px' : '2px'} solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.4);
        "></div>
      `;
      if (canTarget) {
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          setAttackTarget({ driver: d, bonus: pendingAttack });
        });
      }
      const m = new mapboxgl.Marker({ element: el }).setLngLat([d.last_location_lng, d.last_location_lat]).addTo(mapRef.current);
      otherMarkersRef.current[d.user_id] = m;
    });
  }, [otherDrivers, state?.pending_attack]);

  /* ─── Auto-show results screen on first transition to finished ─── */
  useEffect(() => {
    if (state?.driver?.status === 'finished' && !showResults) {
      setShowResults(true);
      loadLeaderboard();
      loadAttacksDealt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.driver?.status]);

  async function loadAttacksDealt() {
    if (!state?.rally?.id || !state?.driver?.user_id) return;
    const { data } = await getRallyBonusLog(state.rally.id);
    if (!data) return;
    const mine = data.filter(b =>
      b.user_id === state.driver.user_id
      && b.seconds_delta > 0
      && b.target_user_id !== null
    );
    const totalSec = mine.reduce((sum, b) => sum + (b.was_blocked_by_shield ? 0 : b.seconds_delta), 0);
    setAttacksDealt({ count: mine.length, totalSec });
  }

  /* ─── Re-render checkpoints/radii when claims change ─── */
  useEffect(() => {
    if (!mapRef.current || !state) return;
    drawCheckpointsAndRadii(mapRef.current, state);
  }, [state]);

  /* ─── Watch position; upload cadence: every ~10s or on movement ─── */
  useEffect(() => {
    if (loading || !state || !state.rally?.id) return;
    if (!('geolocation' in navigator)) return;

    const id = navigator.geolocation.watchPosition(
      (p) => {
        const { latitude: lat, longitude: lng, accuracy } = p.coords;
        if (accuracy && accuracy > 150) return; // drop noisy fixes
        setPosition({ lat, lng, accuracy });
      },
      () => { /* permission denied / error — silent */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
    );
    watchIdRef.current = id;
    return () => { if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, state?.rally?.id]);

  /* ─── Throttled upload + auto-claim loop ─── */
  useEffect(() => {
    if (!position || !state?.rally?.id) return;
    const now = Date.now();
    const sinceLast = now - lastUploadAtRef.current;
    if (sinceLast >= 10000) {
      lastUploadAtRef.current = now;
      updateMyRallyPosition(state.rally.id, position.lat, position.lng, { accuracy: position.accuracy });
    }

    // Update "me" marker on the map
    if (mapRef.current) {
      if (!meMarkerRef.current) {
        const el = document.createElement('div');
        el.style.cssText = `
          width: 18px; height: 18px; border-radius: 50%;
          background: #38bdf8; border: 3px solid white;
          box-shadow: 0 0 0 4px rgba(56,189,248,0.3);
        `;
        meMarkerRef.current = new mapboxgl.Marker({ element: el }).setLngLat([position.lng, position.lat]).addTo(mapRef.current);
      } else {
        meMarkerRef.current.setLngLat([position.lng, position.lat]);
      }
      // Follow-mode: keep the user centered until they pan.
      if (following) {
        mapRef.current.easeTo({ center: [position.lng, position.lat], duration: 600 });
      }
    }

    // Effective radius = checkpoint radius + GPS accuracy fudge (capped at 50m).
    // GPS on a moving phone is often 10–30m noisy, so a 50ft (15m) radius
    // can never trigger without a generous buffer.
    const fudge = Math.min(position.accuracy || 0, 50);

    // Auto-claim if inside an unclaimed checkpoint's effective radius
    const claimedIds = new Set((state.claims || []).map(c => c.checkpoint_id));
    for (const cp of state.checkpoints || []) {
      if (claimedIds.has(cp.id)) continue;
      if (claimingRef.current.has(cp.id)) continue;
      const dMeters = haversineMeters(cp.latitude, cp.longitude, position.lat, position.lng);
      if (dMeters <= cp.radius_feet * 0.3048 + fudge) {
        claimingRef.current.add(cp.id);
        (async () => {
          const { data, error } = await claimCheckpoint(state.rally.id, cp.id, position.lat, position.lng, position.accuracy);
          claimingRef.current.delete(cp.id);
          if (error) {
            // Race / outside-fudge — silently retry on next tick
            return;
          }
          if (data?.expired_attack) {
            flashToast('Pending attack expired — you claimed without using it');
          }
          if (data?.bonus && !data.already_claimed) {
            playCheckpointChime();
            setBonusModal({ checkpointName: cp.name || 'Checkpoint', bonus: data.bonus });
            startSpin(data.bonus);
          } else {
            flashToast(`Checkpoint claimed!`);
          }
          refresh();
          loadLeaderboard();
        })();
      }
    }

    // Auto-finish if all checkpoints claimed and inside finish radius (with same fudge)
    const allClaimed = (state.checkpoints || []).length > 0
      && (state.checkpoints || []).every(cp => claimedIds.has(cp.id));
    if (allClaimed && state.driver?.status === 'active') {
      const dMeters = haversineMeters(state.rally.finish_location_lat, state.rally.finish_location_lng, position.lat, position.lng);
      if (dMeters <= (state.rally.finish_radius_feet || 300) * 0.3048 + fudge) {
        if (!claimingRef.current.has('FINISH')) {
          claimingRef.current.add('FINISH');
          (async () => {
            const { error } = await claimFinish(state.rally.id, position.lat, position.lng, position.accuracy);
            claimingRef.current.delete('FINISH');
            if (error) return;
            playBonusFanfare();
            flashToast('🏁 Finished!');
            refresh();
          })();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, following]);

  /* ─── Elapsed clock ─── */
  useEffect(() => {
    if (!state?.driver?.official_start_time) return;
    const start = new Date(state.driver.official_start_time).getTime();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [state?.driver?.official_start_time]);

  function flashToast(msg) {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  }

  // Slot machine reel: starts fast, decelerates, lands on actual bonus,
  // plays a sound based on what the user got.
  function startSpin(actualBonus) {
    setSpinning(true);
    let i = Math.floor(Math.random() * ALL_BONUS_TYPES.length);
    let elapsed = 0;
    let lastTickAt = 0;

    function delayFor(ms) {
      const t = Math.min(ms / SPIN_TOTAL_MS, 1);
      return 50 + (260 - 50) * Math.pow(t, 1.7); // ramp 50ms → 260ms
    }

    function tick() {
      if (elapsed >= SPIN_TOTAL_MS) {
        setSpinDisplay(actualBonus.bonus_type);
        setSpinning(false);
        // Pick a sound based on what landed
        if (actualBonus.bonus_type === 'no_effect') {
          playNoEffectBlip();
        } else {
          playBonusFanfare();
        }
        return;
      }
      i = (i + 1) % ALL_BONUS_TYPES.length;
      setSpinDisplay(ALL_BONUS_TYPES[i]);
      // Tick sound during deceleration, throttled so it doesn't spam
      if (elapsed > 2400 && elapsed - lastTickAt > 110) {
        playSlotTick();
        lastTickAt = elapsed;
      }
      const d = delayFor(elapsed);
      elapsed += d;
      setTimeout(tick, d);
    }
    tick();
  }

  /* ─── Detect incoming attacks: fire toast + sad sound when penalty grows ─── */
  useEffect(() => {
    const newPenalty = state?.driver?.total_penalty_seconds || 0;
    const prev = prevPenaltySecRef.current;
    if (newPenalty > prev) {
      const delta = newPenalty - prev;
      flashToast(`💥 You got hit! +${formatElapsed(delta)} on your time`);
      playSadAlert();
    }
    prevPenaltySecRef.current = newPenalty;
  }, [state?.driver?.total_penalty_seconds]);

  async function handleDeployAttack() {
    if (!attackTarget) return;
    setDeployingAttack(true);
    const { data, error } = await applyPendingAttack(attackTarget.bonus.id, attackTarget.driver.user_id);
    setDeployingAttack(false);
    setAttackTarget(null);
    if (error) { flashToast('Attack failed: ' + error); return; }
    if (data?.was_blocked_by_shield) {
      flashToast(`🛡 ${attackTarget.driver.display_name} blocked your attack!`);
    } else if (data?.seconds_delta === 0) {
      flashToast(`Attack landed — ${attackTarget.driver.display_name} already at penalty cap`);
    } else {
      flashToast(`💥 +${formatElapsed(data.seconds_delta)} on ${attackTarget.driver.display_name}!`);
    }
    refresh();
    loadLeaderboard();
  }

  async function loadLeaderboard() {
    if (!state?.rally?.id) return;
    const { data } = await getRallyLeaderboard(state.rally.id);
    if (data) setLeaderboard(data);
  }
  useEffect(() => {
    if (showLeaderboard) loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLeaderboard]);

  if (loading) {
    return <FullScreenWrap><p style={{ color: '#aaa' }}>Loading rally...</p></FullScreenWrap>;
  }
  if (!state || !state.rally) {
    return <FullScreenWrap><p style={{ color: '#aaa' }}>Rally not found.</p></FullScreenWrap>;
  }
  if (!state.driver) {
    return <FullScreenWrap><p style={{ color: '#aaa' }}>You are not registered for this rally.</p></FullScreenWrap>;
  }

  const claimedCount = (state.claims || []).length;
  const totalCheckpoints = (state.checkpoints || []).length;
  const isFinished = state.driver.status === 'finished';
  // Admin hasn't pressed the per-driver Start Timer button yet — surface
  // a clear waiting screen instead of the live UI so the driver doesn't
  // think they should be racing.
  const isWaiting = state.driver.status === 'checked_in';

  return (
    <div style={fullScreen}>
      {/* Top bar */}
      <div style={topBar}>
        <button onClick={onClose} style={closeBtn}>✕</button>
        <div style={{ flex: 1, textAlign: 'center', overflow: 'hidden' }}>
          <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ghost Rally</div>
          <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {state.rally.title}
          </div>
        </div>
        <button onClick={() => setShowLeaderboard(true)} style={{ ...closeBtn, marginRight: 4 }}>🏆</button>
        <div style={{ minWidth: 70, textAlign: 'right' }}>
          <div style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
            {isWaiting ? 'Status' : 'Elapsed'}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: isWaiting ? '#fbbf24' : '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {isWaiting ? 'Ready' : formatElapsed(isFinished ? state.driver.raw_elapsed_seconds : elapsedSec)}
          </div>
        </div>
      </div>

      {/* Map fills available space, with overlay controls */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
        <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 6, zIndex: 5 }}>
          <button onClick={handleCycleStyle} style={mapCtrlBtn} title="Switch map style">
            {MAP_STYLES[mapStyleIdx].label}
          </button>
          <button
            onClick={handleCenterOnMe}
            disabled={!position}
            style={{ ...mapCtrlBtn, ...(following ? { background: 'rgba(34,197,94,0.85)', color: '#0a0a0a', borderColor: 'rgba(34,197,94,1)' } : {}) }}
            title={following ? 'Following you. Pan to look around.' : 'Re-center on your location and resume following.'}
          >
            {following ? '📍 Following' : '🎯 Re-center'}
          </button>
        </div>
        {state?.pending_attack && (
          <div style={pendingAttackBanner}>
            🎯 <strong>{bonusLabel(state.pending_attack.bonus_type)}</strong> ready — tap any orange driver pin to deploy. Expires when you claim your next checkpoint.
          </div>
        )}
      </div>

      {/* Bottom bar with progress */}
      <div style={bottomBar}>
        {isWaiting ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>
              ⏳ Waiting for the start signal
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.7)', maxWidth: 320, margin: '0 auto', lineHeight: 1.4 }}>
              You're checked in. The admin will start your timer when it's your turn — your clock starts the instant they do.
            </div>
          </div>
        ) : isFinished ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>🏁 Finished</div>
            <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
              Final time: {formatElapsed(state.driver.adjusted_elapsed_seconds || state.driver.raw_elapsed_seconds)}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#fff' }}>Checkpoints</span>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.7)' }}>{claimedCount} of {totalCheckpoints}</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: totalCheckpoints > 0 ? `${(claimedCount / totalCheckpoints) * 100}%` : '0%',
                background: '#a78bfa', transition: 'width 0.3s',
              }} />
            </div>
            {claimedCount === totalCheckpoints && totalCheckpoints > 0 && (
              <div style={{ marginTop: 8, fontSize: '0.8125rem', color: '#fbbf24', textAlign: 'center' }}>
                🏁 Head to the finish!
              </div>
            )}
            {!position && (
              <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                Waiting for GPS — make sure location is enabled.
              </div>
            )}
          </>
        )}
      </div>

      {toastMsg && (
        <div style={toastStyle}>{toastMsg}</div>
      )}

      {/* Final results modal — auto-opens when status flips to finished */}
      {showResults && state.driver.status === 'finished' && (() => {
        const myRow = leaderboard.find(r => r.user_id === state.driver.user_id);
        const totalBonusSec = myRow?.total_bonus_seconds ?? state.driver.total_bonus_seconds ?? 0;
        const totalPenaltySec = myRow?.total_penalty_seconds ?? state.driver.total_penalty_seconds ?? 0;
        return (
          <div style={modalOverlay} onClick={() => setShowResults(false)}>
            <div style={resultsCard} onClick={e => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Your Result</div>
                <div style={{ fontSize: '3rem', fontWeight: 800, color: '#fbbf24', lineHeight: 1, marginTop: 4 }}>
                  {myRow?.rank ? formatRank(myRow.rank) : '—'}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                  out of {leaderboard.length || '—'} {leaderboard.length === 1 ? 'driver' : 'drivers'}
                </div>
              </div>

              <div style={resultsGrid}>
                <ResultStat label="Adjusted" value={formatElapsed(state.driver.adjusted_elapsed_seconds ?? state.driver.raw_elapsed_seconds)} primary />
                <ResultStat label="Raw" value={formatElapsed(state.driver.raw_elapsed_seconds)} />
                <ResultStat label="Bonuses earned" value={totalBonusSec > 0 ? `−${formatElapsed(totalBonusSec)}` : '—'} good={totalBonusSec > 0} />
                <ResultStat label="Penalties taken" value={totalPenaltySec > 0 ? `+${formatElapsed(totalPenaltySec)}` : '—'} bad={totalPenaltySec > 0} />
                <ResultStat label="Checkpoints" value={`${(state.claims || []).length} / ${(state.checkpoints || []).length}`} />
                <ResultStat label="Attacks dealt" value={attacksDealt.count > 0 ? `${attacksDealt.count} (+${formatElapsed(attacksDealt.totalSec)})` : '—'} bad={attacksDealt.count > 0} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={() => { setShowResults(false); setShowLeaderboard(true); setLbTab('standings'); }} style={{ ...primaryBtn, flex: 1, marginTop: 0 }}>
                  🏆 Standings
                </button>
                <button onClick={() => { setShowResults(false); setShowLeaderboard(true); setLbTab('log'); }} style={{ ...primaryBtn, flex: 1, marginTop: 0, background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                  📜 Bonus Log
                </button>
              </div>
              <button onClick={onClose} style={{ width: '100%', padding: '10px', marginTop: 8, background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: '0.8125rem', cursor: 'pointer' }}>
                Exit Rally View
              </button>
            </div>
          </div>
        );
      })()}

      {/* Bonus modal — Mario-Kart-style slot reveal + tap-to-target prompt */}
      {bonusModal && (
        <div style={modalOverlay} onClick={() => !spinning && setBonusModal(null)}>
          <div style={bonusModalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'center', marginBottom: 6 }}>
              {spinning ? 'Rolling…' : 'Checkpoint Claimed'}
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#fff', textAlign: 'center', marginBottom: 16 }}>
              {bonusModal.checkpointName}
            </div>

            {/* Slot reel */}
            <div style={spinning ? bonusBoxSpinning : bonusBox(bonusModal.bonus)}>
              <div style={{
                fontSize: '3rem', textAlign: 'center', marginBottom: 6,
                transform: spinning ? 'scale(0.95)' : 'scale(1.1)',
                transition: spinning ? 'none' : 'transform 0.3s ease-out',
                filter: spinning ? 'blur(0.5px)' : 'none',
              }}>
                {bonusEmoji(spinning ? spinDisplay : bonusModal.bonus.bonus_type)}
              </div>
              {!spinning && (
                <>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', textAlign: 'center', marginBottom: 4 }}>
                    {bonusLabel(bonusModal.bonus.bonus_type)}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.85)', textAlign: 'center' }}>
                    {bonusModal.bonus.description}
                  </div>
                  {bonusModal.bonus.seconds_delta !== 0 && !bonusModal.bonus.pending && (
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: bonusModal.bonus.seconds_delta < 0 ? '#22c55e' : '#fbbf24', textAlign: 'center', marginTop: 8, fontVariantNumeric: 'tabular-nums' }}>
                      {bonusModal.bonus.seconds_delta < 0 ? '−' : '+'}{formatElapsed(Math.abs(bonusModal.bonus.seconds_delta))}
                    </div>
                  )}
                </>
              )}
              {spinning && (
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 4 }}>
                  one of six prizes…
                </div>
              )}
            </div>

            {/* Action area — tap-to-target instructions for attacks */}
            {!spinning && bonusModal.bonus.pending && (
              <div style={{
                marginTop: 14, padding: '10px 12px',
                background: 'rgba(251,191,36,0.15)',
                border: '1px solid rgba(251,191,36,0.4)',
                borderRadius: 8,
              }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>
                  🎯 Tap a driver on the map to deploy
                </div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
                  Their position is the orange pin. <strong>If you claim your next checkpoint without deploying, this attack expires.</strong>
                </div>
              </div>
            )}

            {!spinning && (
              <button onClick={() => setBonusModal(null)} style={primaryBtn}>
                {bonusModal.bonus.pending ? 'Got it — deploy on map' : 'Continue'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Confirm attack target modal */}
      {attackTarget && (
        <div style={modalOverlay} onClick={() => !deployingAttack && setAttackTarget(null)}>
          <div style={bonusModalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'center', marginBottom: 6 }}>
              Confirm Attack
            </div>
            <div style={{ fontSize: '2rem', textAlign: 'center' }}>{bonusEmoji(attackTarget.bonus.bonus_type)}</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', textAlign: 'center', margin: '6px 0 4px' }}>
              {bonusLabel(attackTarget.bonus.bonus_type)}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: 14 }}>
              Add <strong style={{ color: '#fbbf24' }}>+{formatElapsed(attackTarget.bonus.seconds_delta)}</strong> to <strong>{attackTarget.driver.display_name}</strong>?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setAttackTarget(null)} disabled={deployingAttack} style={{ ...primaryBtn, flex: 1, marginTop: 0, background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                Cancel
              </button>
              <button onClick={handleDeployAttack} disabled={deployingAttack} style={{ ...primaryBtn, flex: 1, marginTop: 0, background: '#fbbf24', color: '#0a0a0a' }}>
                {deployingAttack ? 'Deploying…' : '💥 Attack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard / Log modal */}
      {showLeaderboard && (
        <div style={modalOverlay} onClick={() => setShowLeaderboard(false)}>
          <div style={leaderboardCard} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 6 }}>
                <button onClick={() => setLbTab('standings')} style={tabBtn(lbTab === 'standings')}>🏆 Standings</button>
                <button onClick={() => setLbTab('log')} style={tabBtn(lbTab === 'log')}>📜 Log</button>
              </div>
              <button onClick={() => setShowLeaderboard(false)} style={{ ...closeBtn, padding: '4px 10px' }}>Close</button>
            </div>

            {lbTab === 'log' && (
              <div style={{ marginTop: 4 }}>
                <RallyBonusLog rallyId={state.rally.id} currentUserId={state.driver.user_id} dark />
              </div>
            )}
            {lbTab === 'standings' && leaderboard.length === 0 && (
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', textAlign: 'center', padding: 20 }}>
                Standings appear as drivers progress.
              </p>
            )}
            {lbTab === 'standings' && leaderboard.length > 0 && (
              <div>
                {leaderboard.map(row => {
                  const isMe = row.user_id === state.driver.user_id;
                  return (
                    <div key={row.user_id} style={lbRow(isMe)}>
                      <div style={{ width: 28, fontWeight: 700, color: '#fff' }}>{row.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.display_name}{isMe ? ' (you)' : ''}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.55)' }}>
                          {row.claims_count}/{row.total_checkpoints} CPs
                          {row.shield_active && ' · 🛡'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                          {row.adjusted_elapsed_seconds != null
                            ? formatElapsed(row.adjusted_elapsed_seconds)
                            : (row.status === 'finished' ? formatElapsed(row.raw_elapsed_seconds) : '—')}
                        </div>
                        <div style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.5)' }}>
                          {row.total_bonus_seconds > 0 && <span style={{ color: '#22c55e' }}>−{formatElapsed(row.total_bonus_seconds)} </span>}
                          {row.total_penalty_seconds > 0 && <span style={{ color: '#fbbf24' }}>+{formatElapsed(row.total_penalty_seconds)}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function escapeHTML(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function formatRank(r) {
  if (r === 1) return '🥇 1st';
  if (r === 2) return '🥈 2nd';
  if (r === 3) return '🥉 3rd';
  return `${r}th`;
}

function ResultStat({ label, value, primary, good, bad }) {
  const color = primary ? '#fff' : good ? '#22c55e' : bad ? '#fbbf24' : 'rgba(255,255,255,0.85)';
  return (
    <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: primary ? '1.25rem' : '1rem', fontWeight: 700, color, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function bonusEmoji(type) {
  return ({
    time_cut: '⚡',
    big_time_cut: '🚀',
    shield: '🛡',
    add_1_min: '🎯',
    add_2_min: '💥',
    no_effect: '🎲',
  })[type] || '✨';
}

function bonusLabel(type) {
  return ({
    time_cut: 'Time Cut',
    big_time_cut: 'Big Time Cut',
    shield: 'Shield',
    add_1_min: '+1 Minute Attack',
    add_2_min: '+2 Minute Attack',
    no_effect: 'No Effect',
  })[type] || 'Bonus';
}

function FullScreenWrap({ children }) {
  return (
    <div style={{ ...fullScreen, alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  );
}

/* ─── Helpers ─── */

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6378137;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function formatElapsed(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function circleFeature(lng, lat, radiusFeet, props = {}) {
  const radiusMeters = radiusFeet * 0.3048;
  const segments = 64;
  const earthRadius = 6378137;
  const coords = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    const dx = radiusMeters * Math.cos(angle);
    const dy = radiusMeters * Math.sin(angle);
    const dLat = (dy / earthRadius) * (180 / Math.PI);
    const dLng = (dx / (earthRadius * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: 'Feature', properties: props, geometry: { type: 'Polygon', coordinates: [coords] } };
}

function drawCheckpointsAndRadii(map, state) {
  const { rally, checkpoints, claims } = state;
  const claimedIds = new Set((claims || []).map(c => c.checkpoint_id));

  const features = [];
  if (rally.start_location_lat != null) {
    features.push(circleFeature(rally.start_location_lng, rally.start_location_lat, 100, { kind: 'start' }));
  }
  if (rally.finish_location_lat != null) {
    features.push(circleFeature(rally.finish_location_lng, rally.finish_location_lat, rally.finish_radius_feet || 300, { kind: 'finish' }));
  }
  (checkpoints || []).forEach(cp => {
    features.push(circleFeature(cp.longitude, cp.latitude, cp.radius_feet || 300, {
      kind: claimedIds.has(cp.id) ? 'claimed' : 'unclaimed',
    }));
  });

  const SOURCE = 'rally-active-radii';
  if (map.getSource(SOURCE)) {
    map.getSource(SOURCE).setData({ type: 'FeatureCollection', features });
  } else {
    map.addSource(SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features } });
    map.addLayer({
      id: 'rally-active-radii-fill', type: 'fill', source: SOURCE,
      paint: {
        'fill-color': ['match', ['get', 'kind'],
          'start', '#22c55e',
          'finish', '#fbbf24',
          'claimed', '#22c55e',
          '#a78bfa',
        ],
        'fill-opacity': ['match', ['get', 'kind'], 'claimed', 0.35, 0.18],
      },
    });
    map.addLayer({
      id: 'rally-active-radii-line', type: 'line', source: SOURCE,
      paint: {
        'line-color': ['match', ['get', 'kind'],
          'start', '#22c55e',
          'finish', '#fbbf24',
          'claimed', '#22c55e',
          '#a78bfa',
        ],
        'line-width': 2,
      },
    });
  }

  // Static markers per checkpoint. For OPEN-order rallies we don't show
  // a number — drivers shouldn't think there's a required sequence.
  // Fixed-order rallies still show 1/2/3 to make the next target obvious.
  const isFixedOrder = rally.checkpoint_order_type === 'fixed';
  (checkpoints || []).forEach((cp, i) => {
    const claimed = claimedIds.has(cp.id);
    const el = document.createElement('div');
    el.style.cssText = `
      width: 26px; height: 26px; border-radius: 50%;
      background: ${claimed ? '#22c55e' : '#a78bfa'}; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700; border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4); pointer-events: none;
    `;
    el.textContent = claimed ? '✓' : (isFixedOrder ? String(i + 1) : '●');
    new mapboxgl.Marker({ element: el }).setLngLat([cp.longitude, cp.latitude]).addTo(map);
  });
  if (rally.finish_location_lat != null) {
    const el = document.createElement('div');
    el.style.cssText = `
      width: 30px; height: 30px; border-radius: 50%;
      background: #fbbf24; color: #000;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 800; border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4); pointer-events: none;
    `;
    el.textContent = '🏁';
    new mapboxgl.Marker({ element: el }).setLngLat([rally.finish_location_lng, rally.finish_location_lat]).addTo(map);
  }
}

/* ─── Styles ─── */

const fullScreen = {
  position: 'fixed', inset: 0, zIndex: 3000,
  display: 'flex', flexDirection: 'column',
  background: '#0a0a0a', color: '#fff',
};

const topBar = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px',
  background: 'linear-gradient(180deg, #1a1a1a 0%, rgba(26,26,26,0.95) 100%)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
};

const closeBtn = {
  background: 'rgba(255,255,255,0.1)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6, padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600,
  cursor: 'pointer',
};

const bottomBar = {
  padding: '12px 16px 16px',
  background: 'linear-gradient(0deg, #1a1a1a 0%, rgba(26,26,26,0.95) 100%)',
  borderTop: '1px solid rgba(255,255,255,0.1)',
};

const toastStyle = {
  position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)',
  zIndex: 10,
  background: 'rgba(34,197,94,0.95)', color: '#fff',
  padding: '10px 18px', borderRadius: 8,
  fontSize: '0.875rem', fontWeight: 700,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const modalOverlay = {
  position: 'fixed', inset: 0, zIndex: 4000,
  background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};

const bonusModalCard = {
  background: '#1a1a1a', borderRadius: 14,
  padding: 22, maxWidth: 380, width: '100%',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
};

const leaderboardCard = {
  background: '#1a1a1a', borderRadius: 14,
  padding: 18, maxWidth: 480, width: '100%', maxHeight: '80vh', overflowY: 'auto',
  border: '1px solid rgba(255,255,255,0.1)',
};

const resultsCard = {
  background: 'linear-gradient(180deg, #1a1a1a 0%, #2a1a3a 100%)',
  borderRadius: 16,
  padding: 22, maxWidth: 440, width: '100%',
  border: '1px solid rgba(167,139,250,0.3)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
};

const resultsGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 8,
};

const mapCtrlBtn = {
  padding: '6px 12px', fontSize: '0.75rem', fontWeight: 600,
  background: 'rgba(0,0,0,0.7)', color: '#fff',
  border: '1.5px solid rgba(255,255,255,0.4)',
  borderRadius: 6, cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  whiteSpace: 'nowrap',
};

const primaryBtn = {
  width: '100%', padding: '12px',
  background: '#a78bfa', color: '#1a1a1a', border: 'none',
  borderRadius: 8, fontSize: '0.875rem', fontWeight: 700,
  cursor: 'pointer', marginTop: 16,
};

function bonusBox(bonus) {
  const isPositive = bonus.seconds_delta < 0;
  const isAttack = bonus.seconds_delta > 0 || bonus.pending;
  const bg = isPositive ? 'rgba(34,197,94,0.15)' : isAttack ? 'rgba(251,191,36,0.15)' : 'rgba(167,139,250,0.12)';
  const border = isPositive ? 'rgba(34,197,94,0.4)' : isAttack ? 'rgba(251,191,36,0.4)' : 'rgba(167,139,250,0.3)';
  return {
    background: bg, border: `1px solid ${border}`,
    borderRadius: 10, padding: 16,
  };
}

const bonusBoxSpinning = {
  background: 'rgba(255,255,255,0.06)',
  border: '2px dashed rgba(255,255,255,0.3)',
  borderRadius: 10, padding: 16,
};

const pendingAttackBanner = {
  position: 'absolute', top: 12, left: 110, right: 12,
  zIndex: 6,
  background: 'rgba(251,191,36,0.95)', color: '#0a0a0a',
  padding: '8px 12px', borderRadius: 8,
  fontSize: '0.75rem', lineHeight: 1.4,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  textAlign: 'center',
};

function tabBtn(active) {
  return {
    padding: '6px 12px', fontSize: '0.75rem', fontWeight: 700,
    background: active ? '#fff' : 'transparent',
    color: active ? '#7c3aed' : 'rgba(255,255,255,0.7)',
    border: 'none', borderRadius: 4, cursor: 'pointer',
  };
}

function lbRow(isMe) {
  return {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 8px',
    background: isMe ? 'rgba(167,139,250,0.18)' : 'transparent',
    borderRadius: 6,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  };
}
